import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ApprovedModelsTable from '../approvedModels/ApprovedModelsTable';
import ApprovedModelForm from '../approvedModels/ApprovedModelForm';

const ApprovedModelManagement = ({
  models,
  onCreateModel,
  onUpdateModel,
  onDeactivateModel,
  onDeprecateModel,
  onDeleteModel,
  onCheckUsage,
  onDetectCapabilities
}) => {
  const [formOpen, setFormOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, model: null });
  const [usageDialog, setUsageDialog] = useState({ open: false, data: null });

  const handleAdd = () => {
    setSelectedModel(null);
    setFormOpen(true);
  };

  const handleEdit = (model) => {
    setSelectedModel(model);
    setFormOpen(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedModel) {
        await onUpdateModel(selectedModel.id, data);
      } else {
        await onCreateModel(data);
      }
      setFormOpen(false);
      setSelectedModel(null);
    } catch (err) {
      // Error handled by parent
    }
  };

  const openConfirmDialog = (type, model, title, message) => {
    setConfirmDialog({ open: true, type, model, title, message });
  };

  const handleDeactivate = (model) => {
    openConfirmDialog('deactivate', model, 'Deactivate Model',
      `Are you sure you want to deactivate "${model.name}"? This will prevent it from being used in new configurations.`);
  };

  const handleDeprecate = (model) => {
    openConfirmDialog('deprecate', model, 'Deprecate Model',
      `Are you sure you want to deprecate "${model.name}"? It won't be available for new configurations.`);
  };

  const handleDelete = (model) => {
    openConfirmDialog('delete', model, 'Delete Model',
      `Are you sure you want to delete "${model.name}"? This action cannot be undone.`);
  };

  const handleViewUsage = async (model) => {
    try {
      const usageData = await onCheckUsage(model.id);
      setUsageDialog({ open: true, data: usageData, model });
    } catch (err) {
      // Error handled by parent
    }
  };

  const handleConfirmAction = async () => {
    const { type, model } = confirmDialog;
    try {
      if (type === 'deactivate') {
        await onDeactivateModel(model.id);
      } else if (type === 'deprecate') {
        await onDeprecateModel(model.id);
      } else if (type === 'delete') {
        await onDeleteModel(model.id);
      }
      setConfirmDialog({ open: false, type: null, model: null });
    } catch (err) {
      setConfirmDialog({ open: false, type: null, model: null });
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Manage Approved Bedrock Models
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAdd}
        >
          Add New Model
        </Button>
      </Box>

      <ApprovedModelsTable
        models={models}
        onEdit={handleEdit}
        onDeactivate={handleDeactivate}
        onDeprecate={handleDeprecate}
        onDelete={handleDelete}
        onViewUsage={handleViewUsage}
        onDetectCapabilities={onDetectCapabilities}
      />

      {/* Form Dialog */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedModel ? 'Edit Approved Model' : 'Add New Approved Model'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <ApprovedModelForm
              model={selectedModel}
              onSave={handleSave}
              onCancel={() => setFormOpen(false)}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: null, model: null })}>
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: null, model: null })}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmAction}
            color={confirmDialog.type === 'delete' ? 'error' : 'warning'}
            variant="contained"
          >
            {confirmDialog.type === 'delete' ? 'Delete' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={usageDialog.open} onClose={() => setUsageDialog({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Model Usage: {usageDialog.model?.name}</DialogTitle>
        <DialogContent>
          {usageDialog.data?.isInUse ? (
            <>
              <Typography variant="body1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Global Configuration:
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'primary.50' }}>
                <Typography variant="body2">
                  Used in: {usageDialog.data.globalConfigurationPositions?.join(', ')}
                </Typography>
              </Paper>
            </>
          ) : (
            <Typography variant="body1">
              This model is not currently in use by the global configuration.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsageDialog({ open: false, data: null })}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

ApprovedModelManagement.propTypes = {
  models: PropTypes.array.isRequired,
  onCreateModel: PropTypes.func.isRequired,
  onUpdateModel: PropTypes.func.isRequired,
  onDeactivateModel: PropTypes.func.isRequired,
  onDeprecateModel: PropTypes.func.isRequired,
  onDeleteModel: PropTypes.func.isRequired,
  onCheckUsage: PropTypes.func.isRequired,
  onDetectCapabilities: PropTypes.func.isRequired
};

export default ApprovedModelManagement;
